import React, { useRef, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  Paper,
  Divider,
  useTheme,
  FormHelperText,
} from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon } from '@mui/icons-material';
import { motion, useInView } from 'framer-motion';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { getIOSButtonPrimaryStyles, getGlassCardStyles } from '../../shared/styles';

const contentUseCase = new ContentUseCase();

export const MitmachenPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const content = contentUseCase.getMitmachenContent(language);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [device, setDevice] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [resourceDetails, setResourceDetails] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ name: false, email: false, privacy: false });

  const headerRef = useRef(null);
  const formRef = useRef(null);
  const contactRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });
  const formInView = useInView(formRef, { once: true });
  const contactInView = useInView(contactRef, { once: true });

  const nameError = touched.name && !name.trim();
  const emailError = touched.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const privacyError = touched.privacy && !privacyConsent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, privacy: true });

    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !privacyConsent) return;

    setLoading(true);
    setSubmitError(false);

    try {
      const res = await fetch('/.netlify/functions/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          role,
          device,
          how_heard: howHeard,
          offers_resources: String(resourceDetails.trim().length > 0),
          resource_details: resourceDetails,
          privacy_consent: 'true',
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setSubmitted(true);
    } catch {
      setSubmitError(true);
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const } },
  };

  const w = content.waitlist;

  return (
    <Box>
      <SEO page="mitmachen" />
      <Container maxWidth="sm" sx={{ py: theme.spacing(8) }}>

        {/* Header */}
        <motion.div
          ref={headerRef}
          initial="hidden"
          animate={headerInView ? 'visible' : 'hidden'}
          variants={fadeInUp}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: theme.spacing(2) }}>
            <Box
              component="img"
              src="/icon.png"
              alt=""
              aria-hidden="true"
              sx={{ height: { xs: 44, md: 56 }, width: 'auto' }}
            />
            <Typography variant="h2" component="h1" color="primary" sx={{ fontWeight: 800 }}>
              {content.title}
            </Typography>
          </Box>
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: theme.spacing(1), fontWeight: 500 }}>
            {content.subtitle}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: theme.spacing(5), lineHeight: 1.8 }}>
            {content.description}
          </Typography>
        </motion.div>

        {/* Waitlist form */}
        <motion.div
          ref={formRef}
          initial="hidden"
          animate={formInView ? 'visible' : 'hidden'}
          variants={{ ...fadeInUp, visible: { ...fadeInUp.visible, transition: { duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] as const } } }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getGlassCardStyles(theme),
              p: { xs: theme.spacing(3), sm: theme.spacing(5) },
              borderRadius: 4,
              mb: theme.spacing(8),
            }}
          >
            {submitted ? (
              <Box sx={{ textAlign: 'center', py: theme.spacing(4) }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'primary.main', mb: theme.spacing(2) }} />
                <Typography variant="h5" color="primary" fontWeight={700} gutterBottom>
                  {w.successTitle}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {w.successMessage}
                </Typography>
              </Box>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: theme.spacing(3) }}>
                  {w.title}
                </Typography>

                <TextField
                  label={w.nameLabel + ' *'}
                  placeholder={w.namePlaceholder}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  error={nameError}
                  helperText={nameError ? (language === 'de' ? 'Bitte gib deinen Namen ein.' : 'Please enter your name.') : ''}
                  fullWidth
                  sx={{ mb: theme.spacing(3) }}
                />

                <TextField
                  label={w.emailLabel + ' *'}
                  type="email"
                  placeholder={w.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  error={emailError}
                  helperText={emailError ? (language === 'de' ? 'Bitte gib eine gültige E-Mail-Adresse ein.' : 'Please enter a valid email address.') : ''}
                  fullWidth
                  sx={{ mb: theme.spacing(3) }}
                />

                <FormControl fullWidth sx={{ mb: theme.spacing(3) }}>
                  <InputLabel shrink>{w.roleLabel}</InputLabel>
                  <Select
                    value={role}
                    label={w.roleLabel}
                    onChange={(e) => setRole(e.target.value)}
                    displayEmpty
                    notched
                  >
                    <MenuItem value=""><em>{w.rolePlaceholder}</em></MenuItem>
                    {w.roleOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: theme.spacing(3) }}>
                  <InputLabel shrink>{w.deviceLabel}</InputLabel>
                  <Select
                    value={device}
                    label={w.deviceLabel}
                    onChange={(e) => setDevice(e.target.value)}
                    displayEmpty
                    notched
                  >
                    <MenuItem value=""><em>{w.devicePlaceholder}</em></MenuItem>
                    {w.deviceOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={w.howHeardLabel}
                  placeholder={w.howHeardPlaceholder}
                  value={howHeard}
                  onChange={(e) => setHowHeard(e.target.value)}
                  fullWidth
                  sx={{ mb: theme.spacing(3) }}
                />

                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(1), lineHeight: 1.6 }}>
                    {w.offersResourcesIntro}
                  </Typography>
                  <TextField
                    placeholder={w.offersResourcesPlaceholder}
                    value={resourceDetails}
                    onChange={(e) => setResourceDetails(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                  />
                </Box>

                <FormControl error={privacyError} sx={{ mb: theme.spacing(3) }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={privacyConsent}
                        onChange={(e) => setPrivacyConsent(e.target.checked)}
                        onBlur={() => setTouched((t) => ({ ...t, privacy: true }))}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" color={privacyError ? 'error' : 'text.primary'}>
                        {w.privacyLabel} *
                      </Typography>
                    }
                  />
                  <FormHelperText sx={{ ml: 0, mt: 0.5 }}>
                    {w.privacyNote}
                    <Box component="span" sx={{ fontWeight: 600 }}>{w.privacyContact}</Box>
                  </FormHelperText>
                  {privacyError && (
                    <FormHelperText>
                      {language === 'de' ? 'Bitte stimme der Datenschutzerklärung zu.' : 'Please accept the privacy policy.'}
                    </FormHelperText>
                  )}
                </FormControl>

                {submitError && (
                  <Alert severity="error" sx={{ mb: theme.spacing(3) }}>
                    {w.errorMessage}
                  </Alert>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: theme.spacing(2) }}>
                  {w.requiredHint}
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{ ...getIOSButtonPrimaryStyles(theme), py: 1.5 }}
                >
                  {loading
                    ? (language === 'de' ? 'Wird eingetragen …' : 'Submitting …')
                    : w.submitButton}
                </Button>
              </form>
            )}
          </Paper>
        </motion.div>

        {/* Contact section */}
        <motion.div
          ref={contactRef}
          initial="hidden"
          animate={contactInView ? 'visible' : 'hidden'}
          variants={{ ...fadeInUp, visible: { ...fadeInUp.visible, transition: { duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] as const } } }}
        >
          <Divider sx={{ mb: theme.spacing(6) }} />
          <Typography variant="h5" align="center" fontWeight={700} color="primary" gutterBottom>
            {content.contact.title}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: theme.spacing(3), lineHeight: 1.8, maxWidth: 520, mx: 'auto' }}>
            {content.contact.description}
          </Typography>
          <Typography variant="body1" align="center" fontWeight={600} color="primary.main">
            {content.contact.email}
          </Typography>
        </motion.div>

      </Container>
    </Box>
  );
};
